import React from "react";
import { FileText } from "lucide-react";
import { MobileFlowCard } from "./mobile-flow-layout";

interface ReviewRow {
  label: string;
  value?: React.ReactNode;
}

interface ReviewLine {
  key: React.Key;
  description: string;
  detail?: string;
  amount: number;
}

interface MobileDocumentReviewProps {
  rows: ReviewRow[];
  lines: ReviewLine[];
  currency: string;
  subTotal: number;
  vat: number;
  total: number;
  onPreview: () => void;
  previewLabel?: string;
}

/** Final step of the quotation/invoice flows: a compact summary plus a link to the A4 preview. */
const MobileDocumentReview: React.FC<MobileDocumentReviewProps> = ({
  rows,
  lines,
  currency,
  subTotal,
  vat,
  total,
  onPreview,
  previewLabel = "Preview document",
}) => {
  const visibleRows = rows.filter(
    (row) => row.value !== undefined && row.value !== null && row.value !== "",
  );

  return (
    <>
      <div className="rounded-2xl bg-gray-900 p-4 text-white">
        <p className="text-xs font-medium text-white/60">Total</p>
        <p className="mt-1 break-words text-[26px] font-bold leading-tight tabular-nums font-jakarta">
          {currency} {total.toLocaleString()}
        </p>
        <p className="mt-1 text-xs text-white/60">
          {lines.length} {lines.length === 1 ? "item" : "items"} · Sub Total{" "}
          {currency} {subTotal.toLocaleString()} · VAT {currency}{" "}
          {vat.toLocaleString()}
        </p>
      </div>

      <button
        type="button"
        onClick={onPreview}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white text-sm font-semibold text-gray-800 active:bg-gray-50"
      >
        <FileText className="h-4 w-4" />
        {previewLabel}
      </button>

      {visibleRows.length > 0 && (
        <MobileFlowCard title="Details">
          <dl className="divide-y divide-gray-100">
            {visibleRows.map((row) => (
              <div
                key={row.label}
                className="flex items-start justify-between gap-4 py-2.5 first:pt-0 last:pb-0"
              >
                <dt className="shrink-0 text-[13px] text-gray-500">
                  {row.label}
                </dt>
                <dd className="min-w-0 break-words text-right text-[13px] font-medium text-gray-900">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        </MobileFlowCard>
      )}

      <MobileFlowCard title="Items">
        {lines.length === 0 ? (
          <p className="text-sm text-gray-500">No items added.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {lines.map((line) => (
              <li
                key={line.key}
                className="flex items-start justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="break-words text-[13px] font-medium text-gray-900">
                    {line.description}
                  </p>
                  {line.detail && (
                    <p className="text-xs text-gray-500">{line.detail}</p>
                  )}
                </div>
                <p className="shrink-0 text-[13px] font-semibold tabular-nums text-gray-900">
                  {line.amount.toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        )}
      </MobileFlowCard>
    </>
  );
};

export default MobileDocumentReview;
